import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_SVG_PATHS = [
  "M10 5 L10 75",
  "M70 5 L70 75",
  "M10 40 L70 40",
  "M10 5 L40 5",
  "M50 5 L70 15",
];

const DATA_LINES = [
  "LOCATION: KOYE FACHE, ADDIS ABABA",
  "SERVICES: CONSTRUCTION \u00B7 ELECTRICAL \u00B7 PLUMBING \u00B7 SUPPLY",
  "STATUS: SYSTEMS ONLINE",
];

export default function LoadingScreen({ onComplete }) {
  const [phase, setPhase] = useState(0);
  const [progress, setProgress] = useState(0);
  const [typedLines, setTypedLines] = useState(['', '', '']);
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const seen = sessionStorage.getItem('holistic_loaded');
    if (seen) { onComplete?.(); return; }

    const timers = [];
    timers.push(setTimeout(() => setPhase(1), 100));
    timers.push(setTimeout(() => setPhase(2), 600));
    timers.push(setTimeout(() => setPhase(3), 1400));
    timers.push(setTimeout(() => setPhase(4), 1800));
    timers.push(setTimeout(() => {
      setPhase(5);
      const start = Date.now();
      const interval = setInterval(() => {
        const p = Math.min(((Date.now() - start) / 1000) * 100, 100);
        setProgress(Math.round(p));
        if (p >= 100) clearInterval(interval);
      }, 30);
      timerRef.current = interval;
    }, 2200));
    timers.push(setTimeout(() => setPhase(6), 2600));
    timers.push(setTimeout(() => setExiting(true), 3500));
    timers.push(setTimeout(() => {
      sessionStorage.setItem('holistic_loaded', '1');
      onComplete?.();
    }, 4200));

    return () => {
      timers.forEach(clearTimeout);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [onComplete]);

  useEffect(() => {
    if (phase < 6) return;
    DATA_LINES.forEach((line, lineIdx) => {
      const chars = line.split('');
      chars.forEach((_, charIdx) => {
        setTimeout(() => {
          setTypedLines(prev => {
            const next = [...prev];
            next[lineIdx] = line.slice(0, charIdx + 1);
            return next;
          });
        }, lineIdx * 400 + charIdx * 22);
      });
    });
  }, [phase]);

  return (
    <AnimatePresence>
      {!exiting ? null : null}
      <motion.div
        data-testid="loading-screen"
        className="fixed inset-0 z-[10000] flex flex-col items-center justify-center overflow-hidden"
        style={{ background: '#050608' }}
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Blueprint grid */}
        <motion.div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              repeating-linear-gradient(0deg, transparent, transparent 79px, rgba(30,111,217,0.08) 79px, rgba(30,111,217,0.08) 80px),
              repeating-linear-gradient(90deg, transparent, transparent 79px, rgba(30,111,217,0.08) 79px, rgba(30,111,217,0.08) 80px)
            `,
            backgroundSize: '80px 80px',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 1 ? 1 : 0 }}
          transition={{ duration: 0.8 }}
        />

        <motion.div
          className="relative flex flex-col items-center"
          animate={exiting ? { scale: 0.85, opacity: 0, filter: 'blur(8px)' } : {}}
          transition={{ duration: 0.4 }}
        >
          {/* SVG Logo */}
          <svg width="80" height="80" viewBox="0 0 80 80" className="mb-6">
            {LOGO_SVG_PATHS.map((d, i) => (
              <motion.path
                key={i}
                d={d}
                stroke="#E8A020"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={phase >= 2 ? { pathLength: 1 } : {}}
                transition={{ duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              />
            ))}
          </svg>

          {/* HOLISTIC */}
          <div className="flex overflow-hidden mb-2">
            {'HOLISTIC'.split('').map((char, i) => (
              <motion.span
                key={i}
                className="font-sans font-bold text-white text-4xl sm:text-5xl"
                style={{ letterSpacing: '0.25em' }}
                initial={{ y: 24, opacity: 0 }}
                animate={phase >= 3 ? { y: 0, opacity: 1 } : {}}
                transition={{ type: 'spring', stiffness: 130, damping: 16, delay: i * 0.06 }}
              >
                {char}
              </motion.span>
            ))}
          </div>

          {/* Subtitle */}
          <motion.p
            className="font-sans font-normal text-xs uppercase"
            style={{ color: '#E8A020', letterSpacing: '0.4em' }}
            initial={{ y: 8, opacity: 0 }}
            animate={phase >= 4 ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.5 }}
          >
            GENERAL TRADING WORKS PLC
          </motion.p>

          {/* Progress bar */}
          {phase >= 5 && (
            <div className="mt-6 flex items-center gap-3">
              <div className="w-60 h-0.5 bg-white/10 overflow-hidden rounded-full">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: '#E8A020' }}
                  initial={{ width: '0%' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.05 }}
                />
              </div>
              <span className="font-sans text-xs" style={{ color: 'rgba(232,160,32,0.6)' }}>
                {progress}%
              </span>
            </div>
          )}

          {/* Data lines */}
          {phase >= 6 && (
            <div className="mt-5 space-y-1">
              {typedLines.map((line, i) => (
                <p key={i} className="font-sans font-light text-xs" style={{ color: 'rgba(232,160,32,0.35)', letterSpacing: '0.15em' }}>
                  {line}
                  {i === 2 && line.includes('ONLINE') && (
                    <motion.span
                      className="ml-1"
                      style={{ color: '#10B981' }}
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      &#10003;
                    </motion.span>
                  )}
                </p>
              ))}
            </div>
          )}
        </motion.div>

        {/* Industrial wipe exit panels */}
        {exiting && (
          <div className="absolute inset-0 z-10 flex flex-col">
            {[0, 1, 2, 3, 4].map(i => (
              <motion.div
                key={i}
                className="flex-1"
                style={{ background: '#050608' }}
                initial={{ x: 0 }}
                animate={{ x: i % 2 === 0 ? '-110vw' : '110vw' }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.04,
                  ease: [0.77, 0, 0.175, 1],
                }}
              />
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
